<template>
  <VueTagsInput
    v-model="tag"
    :add-from-paste="false"
    :add-only-from-autocomplete="shouldAddOnlyFromAutoComplete"
    :autocomplete-items="autocompleteItems"
    :disabled="!hasNormalAccess || disable"
    :placeholder="placeholder"
    :tags="selectedSources"
    @paste="onPaste"
    @tags-changed="update"
  >
    <template #autocomplete-item="props">
      <div @click="props.performAdd(props.item)">
        {{ props.item.id }} - {{ props.item.name }}
        <span
          v-if="props.item.location"
          style="font-size: 80%"
        >
          - {{ props.item.location }}
        </span>
      </div>
    </template>
  </VueTagsInput>
</template>

<script>
import { VueTagsInput } from "@sipec/vue3-tags-input";
import { debounce, uniqBy } from "lodash-es";
import { storeToRefs } from "pinia";

import { sortByStartMatches } from "shared/helpers/array";
import { has } from "shared/helpers/object";
import {
  Magazine,
  Newspaper,
  Podcast,
  RadioStation,
  SourceGroup,
  TvChannel,
  Website,
} from "shared/resources";

import { useUserStore } from "src/stores/user";

export default {
  name: "FilterSourceInput",
  components: {
    VueTagsInput,
  },
  props: {
    modelValue: {
      type: Array,
      required: true,
    },
    action: {
      type: String,
      required: true,
      validator(value) {
        return ["include", "exclude"].includes(value);
      },
    },
    disable: {
      type: Boolean,
    },
    placeholder: {
      type: String,
      default: "Add Tag",
    },
    textTypes: {
      type: Array,
      required: true,
    },
    targetType: {
      type: String,
      required: true,
    },
    scope: {
      type: String,
      required: true,
    },
    resource: {
      type: String,
      required: true,
    },
  },
  emits: ["update:modelValue"],
  setup() {
    const userStore = useUserStore();
    const { hasNormalAccess } = storeToRefs(userStore);

    return { hasNormalAccess };
  },
  data() {
    return {
      tag: "",
      autocompleteItems: [],
      addOnlyFromAutocomplete: false,
      resources: {
        websites: Website.includes("location"),
        magazines: Magazine.includes("location"),
        newspapers: Newspaper.includes("location"),
        tv_channels: TvChannel.includes("location"),
        radio_stations: RadioStation.includes("location"),
        podcasts: Podcast,
      },
    };
  },
  computed: {
    selectedSources: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    shouldAddOnlyFromAutoComplete() {
      return (
        Boolean(this.selectedSources.length) && this.addOnlyFromAutocomplete
      );
    },
  },
  watch: {
    tag() {
      if (!this.isJson(this.tag)) {
        this.debouncedGetSources();
      }
    },
  },
  mounted() {
    this.debouncedGetSources = debounce(this.getSources, 600);
  },
  methods: {
    isJson(str) {
      if (Number(str)) return false;

      try {
        JSON.parse(str);
      } catch {
        return false;
      }

      return true;
    },
    onPaste(event) {
      const text = event.clipboardData.getData("text");

      if (!this.isJson(text)) return;

      this.selectedSources = JSON.parse(text);

      setTimeout(() => {
        this.tag = "";
      }, 100);
    },
    update(newTags) {
      try {
        const mappedTags = newTags
          .flatMap((tag) => (has(tag, "id") ? tag : JSON.parse(tag.text)))
          .map((tag) => ({
            ...tag,
            action: this.action,
          }));

        this.selectedSources = uniqBy(mappedTags, "id");
      } catch {
        this.addOnlyFromAutocomplete = true;
        this.selectedSources = newTags;
      }

      if (newTags.length === 0) {
        this.addOnlyFromAutocomplete = false;
      }
    },
    async getSources() {
      try {
        const params = { query: this.tag };

        let results = "";

        if (this.targetType === "SourceGroup") {
          results = (
            await SourceGroup.where({
              name: { match: params.query.replace("&", "%26") },
            })
              .per(100)
              .page(1)
              .all()
          ).data;
        } else {
          const filter = { active: true };

          if (Number(params.query)) {
            filter.id = params.query;
          } else {
            filter.search = { match: params.query.replace("&", "%26") };
          }

          results = (
            await this.resources[this.resource].where(filter).per(100).all()
          ).data;

          results = results.map((result) => ({
            ...result,
            location: result.location?.name,
          }));
        }

        const autocompleteItems = results.map((source) => ({
          ...source,
          action: this.action,
          text: this.textTypes
            .reduce(
              (acc, key) => `${acc} ${source[key] ? source[key] : ""} - `,
              ""
            )
            .trim()
            .replace(/-$/, ""),
          target_id: source.id,
          target_type: this.targetType,
          scope: this.scope,
          classes: `tag-${this.scope}`,
        }));

        this.autocompleteItems = sortByStartMatches(
          autocompleteItems,
          this.tag,
          "text",
          100
        ).filter((result) => result.id);
      } catch {
        this.$q.notify(
          "There was an error retrieving sources. Please try again"
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ti-disabled {
  background-color: #ddd !important;
}

:deep(.ti-input) {
  width: 100%;
}

:deep(.ti-selected-item) {
  background-color: $primary !important;
}

:deep(.ti-tag) {
  &.tag-online {
    background-color: $online;
  }

  &.tag-print {
    background-color: $print;
  }

  &.tag-magazine {
    background-color: $magazine;
  }

  &.tag-tv {
    background-color: $tv;
  }

  &.tag-radio {
    background-color: $radio;
  }

  &.tag-podcast {
    background-color: $podcast;
  }
}
</style>
